import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { formatMentions } from "../../utils/formatMentions";
import {
  FiHeart,
  FiMessageCircle,
  FiShare2,
  FiCheckSquare,
  FiCheckCircle,
  FiMail,
  FiUsers,
  FiBriefcase,
  FiTrash2,
  FiMoreHorizontal,
  FiCheck,
  FiX,
  FiWifiOff,
  FiAlertTriangle,
  FiRefreshCw,
  FiLink,
  FiUserPlus,
  FiStar,
  FiAward,
  FiEye,
  FiAtSign,
  FiHome,
  FiUserMinus,
  FiLogOut,
  FiSettings,
  FiCalendar,
  FiVideo,
  FiBell,
  FiCornerUpLeft,
  FiMessageSquare,
  FiTrendingUp,
  FiTrendingDown,
  FiLock,
  FiFlag,
  FiVolumeX,
  FiSlash,
  FiShield,
  FiThumbsUp,
  FiThumbsDown,
  FiFile,
  FiClock,
  FiCreditCard,
  FiRadio,
  FiXCircle,
  FiUserCheck,
  FiInbox,
  FiArchive,
  FiBookOpen,
} from "react-icons/fi";
import "./NotificationsOverlay.css";
import notificationService from "../../services/notificationService";
import { useUserContext } from "../../context/UserContext";
import { useNotificationContext } from "../../context/NotificationContext";
import DOMPurify from "dompurify";
import { toast } from "react-toastify";
import {
  WORKSHOP_NOTIFICATION_TYPES_ARRAY,
  isWorkshopNotificationType,
} from "../workshops/utils/workshopNotificationUtils";
import {
  COLLABORATOR_NOTIFICATION_TYPES_ARRAY,
  COLLABORATOR_NOTIFICATION_TYPES,
} from "../../constants/collaboratorNotificationConstants";
import {
  isCollaboratorNotificationType,
  getCollaboratorActionUrl,
} from "../collaborators/utils/collaboratorNotificationUtils";

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

const FILTER_OPTIONS = [
  { id: "all", label: "All", icon: FiInbox },
  { id: "feed", label: "Feed", icon: FiHeart },
  { id: "tasks", label: "Tasks", icon: FiCheckSquare },
  { id: "messages", label: "Messages", icon: FiMail },
  { id: "collaboration", label: "Network", icon: FiUsers },
  { id: "freelance", label: "Freelance", icon: FiBriefcase },
  { id: "rooms", label: "Rooms", icon: FiHome },
  { id: "communities", label: "Communities", icon: FiMessageSquare },
  { id: "workshops", label: "Workshops", icon: FiBookOpen },
];

const NOTIFICATION_TYPE_MAP = {
  feed: ["like_post", "comment_post", "reply_comment", "like_comment", "repost"],
  tasks: ["room_task_assigned", "room_task_completed", "task_assigned", "task_updated"],
  messages: ["message", "message_reply"],
  collaboration: [
    "collaboration_request", "collaboration_response", "new_follower",
    "new_rating", "rating_response", "skill_endorsement", "endorsement_response",
    "profile_viewer", ...COLLABORATOR_NOTIFICATION_TYPES_ARRAY,
  ],
  freelance: ["freelance_offer"],
  rooms: [
    "join_request_received", "join_request_accepted", "join_request_rejected",
    "member_removed", "member_left", "member_joined", "room_deleted", "room_updated",
    "event_created", "event_updated", "event_deleted", "event_starting_soon",
    "mention", "file_uploaded", "resource_added", "meeting_scheduled", "meeting_reminder",
  ],
  communities: [
    "community_thread_reply", "community_reply_to_reply", "community_thread_upvote",
    "community_thread_pinned", "community_thread_locked", "community_reaction_received",
    "community_thread_update", "community_mention", "community_chat_mention",
    "community_join_request", "community_join_accepted", "community_join_rejected",
    "community_member_promoted", "community_member_demoted", "community_member_removed",
    "community_moderation_warn", "community_moderation_mute", "community_moderation_ban",
    "community_content_removed", "community_new_report", "community_new_thread",
    "community_settings_updated", "community_announcement", "community_new_room",
  ],
  workshops: WORKSHOP_NOTIFICATION_TYPES_ARRAY,
};

const TIME_LABELS = {
  today: "Today",
  yesterday: "Yesterday",
  thisWeek: "This Week",
  earlier: "Earlier",
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const groupNotificationsByTime = (notifications) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const thisWeek = new Date(today);
  thisWeek.setDate(thisWeek.getDate() - 7);

  return notifications.reduce(
    (groups, notif) => {
      const date = new Date(notif.createdAt);
      if (date >= today) groups.today.push(notif);
      else if (date >= yesterday) groups.yesterday.push(notif);
      else if (date >= thisWeek) groups.thisWeek.push(notif);
      else groups.earlier.push(notif);
      return groups;
    },
    { today: [], yesterday: [], thisWeek: [], earlier: [] }
  );
};

const sanitizeContent = (content) => {
  if (!content) return "";
  return DOMPurify.sanitize(content);
};

// ============================================================================
// NOTIFICATION ICON COMPONENT
// ============================================================================

const NotificationIcon = ({ type }) => {
  const iconMap = {
    // Feed
    like_post: { icon: FiHeart, color: "ntf-icon--red" },
    comment_post: { icon: FiMessageCircle, color: "ntf-icon--blue" },
    reply_comment: { icon: FiCornerUpLeft, color: "ntf-icon--blue" },
    like_comment: { icon: FiHeart, color: "ntf-icon--red" },
    repost: { icon: FiShare2, color: "ntf-icon--green" },
    
    // Tasks
    room_task_assigned: { icon: FiCheckSquare, color: "ntf-icon--indigo" },
    task_assigned: { icon: FiCheckSquare, color: "ntf-icon--indigo" },
    room_task_completed: { icon: FiCheckCircle, color: "ntf-icon--green" },
    task_updated: { icon: FiCheckCircle, color: "ntf-icon--green" },
    
    // Messages
    message: { icon: FiMail, color: "ntf-icon--cyan" },
    message_reply: { icon: FiMail, color: "ntf-icon--cyan" },
    
    // Collaboration
    collaboration_request: { icon: FiUsers, color: "ntf-icon--amber" },
    collaboration_response: { icon: FiUserCheck, color: "ntf-icon--amber" },
    new_follower: { icon: FiUserPlus, color: "ntf-icon--green" },
    new_rating: { icon: FiStar, color: "ntf-icon--amber" },
    rating_response: { icon: FiStar, color: "ntf-icon--amber" },
    skill_endorsement: { icon: FiAward, color: "ntf-icon--indigo" },
    endorsement_response: { icon: FiAward, color: "ntf-icon--indigo" },
    profile_viewer: { icon: FiEye, color: "ntf-icon--slate" },
    mention: { icon: FiAtSign, color: "ntf-icon--blue" },
    
    // Freelance
    freelance_offer: { icon: FiBriefcase, color: "ntf-icon--pink" },
    
    // Research Rooms
    join_request_received: { icon: FiHome, color: "ntf-icon--indigo" },
    join_request_accepted: { icon: FiCheckCircle, color: "ntf-icon--green" },
    join_request_rejected: { icon: FiXCircle, color: "ntf-icon--red" },
    member_removed: { icon: FiUserMinus, color: "ntf-icon--red" },
    member_left: { icon: FiLogOut, color: "ntf-icon--slate" },
    member_joined: { icon: FiUserPlus, color: "ntf-icon--green" },
    room_deleted: { icon: FiTrash2, color: "ntf-icon--red" },
    room_updated: { icon: FiSettings, color: "ntf-icon--slate" },
    event_created: { icon: FiCalendar, color: "ntf-icon--indigo" },
    event_updated: { icon: FiCalendar, color: "ntf-icon--blue" },
    event_deleted: { icon: FiCalendar, color: "ntf-icon--red" },
    event_starting_soon: { icon: FiVideo, color: "ntf-icon--green" },
    meeting_reminder: { icon: FiVideo, color: "ntf-icon--green" },
    meeting_scheduled: { icon: FiCalendar, color: "ntf-icon--indigo" },
    file_uploaded: { icon: FiFile, color: "ntf-icon--blue" },
    resource_added: { icon: FiLink, color: "ntf-icon--blue" },
    
    // Communities
    community_thread_reply: { icon: FiCornerUpLeft, color: "ntf-icon--blue" },
    community_reply_to_reply: { icon: FiMessageSquare, color: "ntf-icon--blue" },
    community_thread_upvote: { icon: FiTrendingUp, color: "ntf-icon--green" },
    community_thread_pinned: { icon: FiFlag, color: "ntf-icon--amber" },
    community_thread_locked: { icon: FiLock, color: "ntf-icon--slate" },
    community_reaction_received: { icon: FiAward, color: "ntf-icon--amber" },
    community_thread_update: { icon: FiBell, color: "ntf-icon--blue" },
    community_mention: { icon: FiAtSign, color: "ntf-icon--blue" },
    community_chat_mention: { icon: FiAtSign, color: "ntf-icon--cyan" },
    community_join_request: { icon: FiHome, color: "ntf-icon--indigo" },
    community_join_accepted: { icon: FiCheckCircle, color: "ntf-icon--green" },
    community_join_rejected: { icon: FiXCircle, color: "ntf-icon--red" },
    community_member_promoted: { icon: FiTrendingUp, color: "ntf-icon--green" },
    community_member_demoted: { icon: FiTrendingDown, color: "ntf-icon--amber" },
    community_member_removed: { icon: FiSlash, color: "ntf-icon--red" },
    community_moderation_warn: { icon: FiAlertTriangle, color: "ntf-icon--amber" },
    community_moderation_mute: { icon: FiVolumeX, color: "ntf-icon--amber" },
    community_moderation_ban: { icon: FiSlash, color: "ntf-icon--red" },
    community_content_removed: { icon: FiTrash2, color: "ntf-icon--red" },
    community_new_report: { icon: FiFlag, color: "ntf-icon--red" },
    community_new_thread: { icon: FiMessageSquare, color: "ntf-icon--blue" },
    community_settings_updated: { icon: FiSettings, color: "ntf-icon--slate" },
    community_announcement: { icon: FiRadio, color: "ntf-icon--indigo" },
    community_new_room: { icon: FiHome, color: "ntf-icon--green" },
    
    // Collaborator types
    [COLLABORATOR_NOTIFICATION_TYPES.NEW_FOLLOWER]: { icon: FiUserPlus, color: "ntf-icon--green" },
    [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_RECEIVED]: { icon: FiUserCheck, color: "ntf-icon--indigo" },
    [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_ACCEPTED]: { icon: FiCheckCircle, color: "ntf-icon--green" },
    [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_REJECTED]: { icon: FiXCircle, color: "ntf-icon--red" },
    [COLLABORATOR_NOTIFICATION_TYPES.RATING_RECEIVED]: { icon: FiStar, color: "ntf-icon--amber" },
    [COLLABORATOR_NOTIFICATION_TYPES.RATING_RESPONSE_RECEIVED]: { icon: FiStar, color: "ntf-icon--amber" },
    [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_RECEIVED]: { icon: FiAward, color: "ntf-icon--indigo" },
    [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_ACCEPTED]: { icon: FiThumbsUp, color: "ntf-icon--green" },
    [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_DECLINED]: { icon: FiThumbsDown, color: "ntf-icon--slate" },
    
    // Workshop types
    workshop_application: { icon: FiUserPlus, color: "ntf-icon--indigo" },
    application_approved: { icon: FiCheckCircle, color: "ntf-icon--green" },
    application_rejected: { icon: FiXCircle, color: "ntf-icon--red" },
    application_pending: { icon: FiClock, color: "ntf-icon--amber" },
    payment_proof_uploaded: { icon: FiCreditCard, color: "ntf-icon--blue" },
    payment_approved: { icon: FiCheckCircle, color: "ntf-icon--green" },
    payment_rejected: { icon: FiXCircle, color: "ntf-icon--red" },
    workshop_reminder_1day: { icon: FiClock, color: "ntf-icon--amber" },
    workshop_reminder_1hour: { icon: FiBell, color: "ntf-icon--amber" },
    session_reminder: { icon: FiClock, color: "ntf-icon--amber" },
    workshop_starting_soon: { icon: FiBell, color: "ntf-icon--green" },
    resource_uploaded: { icon: FiFile, color: "ntf-icon--blue" },
    announcement_posted: { icon: FiRadio, color: "ntf-icon--indigo" },
    schedule_updated: { icon: FiCalendar, color: "ntf-icon--blue" },
    new_discussion: { icon: FiMessageSquare, color: "ntf-icon--blue" },
    discussion_reply: { icon: FiCornerUpLeft, color: "ntf-icon--blue" },
    workshop_cancelled: { icon: FiSlash, color: "ntf-icon--red" },
    workshop_completed: { icon: FiAward, color: "ntf-icon--green" },
    feedback_request: { icon: FiStar, color: "ntf-icon--amber" },
    co_organizer_added: { icon: FiUserCheck, color: "ntf-icon--indigo" },
    participant_joined: { icon: FiUserPlus, color: "ntf-icon--green" },
    workshop_participant_joined: { icon: FiUserPlus, color: "ntf-icon--green" },
    participant_removed: { icon: FiUserMinus, color: "ntf-icon--red" },
  };

  const config = iconMap[type] || { icon: FiBell, color: "ntf-icon--slate" };
  const IconComponent = config.icon;

  return (
    <div className={`ntf-icon ${config.color}`}>
      <IconComponent />
    </div>
  );
};

// ============================================================================
// CONNECTION STATUS COMPONENT
// ============================================================================

const ConnectionStatus = ({ status }) => {
  if (status === "connected") return null;

  const statusConfig = {
    connecting: { icon: FiRefreshCw, text: "Connecting...", className: "ntf-status--connecting" },
    disconnected: { icon: FiWifiOff, text: "Disconnected", className: "ntf-status--disconnected" },
    error: { icon: FiAlertTriangle, text: "Connection Error", className: "ntf-status--error" },
    failed: { icon: FiAlertTriangle, text: "Connection Failed", className: "ntf-status--error" },
  };

  const config = statusConfig[status] || statusConfig.disconnected;
  const IconComponent = config.icon;

  return (
    <div className={`ntf-status ${config.className}`}>
      <IconComponent className={status === "connecting" ? "ntf-spin" : ""} />
      <span>{config.text}</span>
    </div>
  );
};

// ============================================================================
// NOTIFICATION ITEM COMPONENT
// ============================================================================

const NotificationItem = ({
  notification,
  onMarkAsRead,
  onDelete,
  onAction,
  onCollaboratorAction,
  onNavigate,
  actionLoading,
  dropdownOpen,
  onDropdownToggle,
}) => {
  const navigate = useNavigate();
  const { data = {} } = notification;

  const handleClick = () => {
    onNavigate(notification);
  };

  const renderActionButtons = () => {
    const isLoading = actionLoading[`action_${notification._id}`];
    const type = notification.type;

    // Collaboration request actions
    if (type === "collaboration_request" || type === COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_RECEIVED) {
      return (
        <div className="ntf-actions">
          <button
            className="ntf-btn ntf-btn--accept"
            onClick={(e) => {
              e.stopPropagation();
              if (type === COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_RECEIVED) {
                onCollaboratorAction(notification, "accept_collab_request");
              } else {
                onAction(notification, "accept_collaboration");
              }
            }}
            disabled={isLoading}
          >
            <FiCheck /> Accept
          </button>
          <button
            className="ntf-btn ntf-btn--decline"
            onClick={(e) => {
              e.stopPropagation();
              if (type === COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_RECEIVED) {
                onCollaboratorAction(notification, "decline_collab_request");
              } else {
                onAction(notification, "decline_collaboration");
              }
            }}
            disabled={isLoading}
          >
            Decline
          </button>
        </div>
      );
    }

    // Endorsement actions
    if (type === "skill_endorsement" || type === COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_RECEIVED) {
      return (
        <div className="ntf-actions">
          <button
            className="ntf-btn ntf-btn--accept"
            onClick={(e) => {
              e.stopPropagation();
              if (type === COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_RECEIVED) {
                onCollaboratorAction(notification, "accept_endorsement");
              } else {
                onAction(notification, "accept_endorsement");
              }
            }}
            disabled={isLoading}
          >
            <FiCheck /> Accept
          </button>
          <button
            className="ntf-btn ntf-btn--decline"
            onClick={(e) => {
              e.stopPropagation();
              if (type === COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_RECEIVED) {
                onCollaboratorAction(notification, "decline_endorsement");
              } else {
                onAction(notification, "decline_endorsement");
              }
            }}
            disabled={isLoading}
          >
            Decline
          </button>
        </div>
      );
    }

    // Follow back action
    if (type === "new_follower" || type === COLLABORATOR_NOTIFICATION_TYPES.NEW_FOLLOWER) {
      return (
        <div className="ntf-actions">
          <button
            className="ntf-btn ntf-btn--primary"
            onClick={(e) => {
              e.stopPropagation();
              onAction(notification, "follow_back");
            }}
            disabled={isLoading}
          >
            <FiUserPlus /> Follow Back
          </button>
        </div>
      );
    }

    // Meeting/Event actions
    if (type === "event_starting_soon" || type === "meeting_reminder") {
      const meetingLinks = data?.meetingLinks;
      const getMeetingUrl = () => {
        if (meetingLinks?.zoom?.url) return meetingLinks.zoom.url;
        if (meetingLinks?.googleMeet?.url) return meetingLinks.googleMeet.url;
        if (meetingLinks?.teams?.url) return meetingLinks.teams.url;
        if (meetingLinks?.custom?.url) return meetingLinks.custom.url;
        return null;
      };
      const meetingUrl = getMeetingUrl();

      return (
        <div className="ntf-actions">
          {meetingUrl && (
            <button
              className="ntf-btn ntf-btn--accept"
              onClick={(e) => {
                e.stopPropagation();
                window.open(meetingUrl, "_blank");
              }}
            >
              <FiVideo /> Join
            </button>
          )}
        </div>
      );
    }

    // Workshop actions
    if (isWorkshopNotificationType(type)) {
      const workshopId = notification.workshop?._id || data?.workshopId;
      if (!workshopId) return null;

      if (type === "workshop_reminder_1hour" || type === "workshop_starting_soon") {
        return (
          <div className="ntf-actions">
            <button
              className="ntf-btn ntf-btn--accept"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/workshops/${workshopId}`);
              }}
            >
              <FiBell /> Join Now
            </button>
          </div>
        );
      }

      if (type === "feedback_request") {
        return (
          <div className="ntf-actions">
            <button
              className="ntf-btn ntf-btn--primary"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/workshops/${workshopId}/feedback`);
              }}
            >
              <FiStar /> Feedback
            </button>
          </div>
        );
      }
    }

    // Room join request action
    if (type === "join_request_received") {
      return (
        <div className="ntf-actions">
          <button
            className="ntf-btn ntf-btn--primary"
            onClick={(e) => {
              e.stopPropagation();
              const roomId = data?.roomId || notification.researchRoom;
              if (roomId) navigate(`/research-rooms/${roomId}`);
            }}
          >
            <FiEye /> Review
          </button>
        </div>
      );
    }

    // Community join request (moderator)
    if (type === "community_join_request") {
      return (
        <div className="ntf-actions">
          <button
            className="ntf-btn ntf-btn--primary"
            onClick={(e) => {
              e.stopPropagation();
              const communityId = data?.communityId || notification.community;
              if (communityId) navigate(`/communities/${communityId}/moderation/requests`);
            }}
          >
            <FiShield /> Review
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className={`ntf-item ${!notification.read ? "ntf-item--unread" : ""}`}
      onClick={handleClick}
    >
      <div className="ntf-item__content">
        <NotificationIcon type={notification.type} />
        
        <div className="ntf-item__body">
          <div className="ntf-item__header">
            <div className="ntf-item__user">
              {notification.sender?.profilePhotoUrl ? (
                <img
                  src={notification.sender.profilePhotoUrl}
                  alt=""
                  className="ntf-avatar"
                />
              ) : (
                <div className="ntf-avatar ntf-avatar--placeholder">
                  {notification.sender?.name?.charAt(0)?.toUpperCase() || "U"}
                </div>
              )}
              <span className="ntf-item__name">
                {notification.sender?.name || "User"}
              </span>
            </div>
            <span className="ntf-item__time">
              {notification.timestamp || new Date(notification.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          
          <p
            className="ntf-item__message"
            dangerouslySetInnerHTML={{ __html: sanitizeContent(notification.message) }}
          />
          
          {notification.post?.content && (
            <p className="ntf-item__preview">
              "{formatMentions(notification.post.content.substring(0, 60))}..."
            </p>
          )}
          
          {renderActionButtons()}
        </div>
      </div>

      <div className="ntf-item__menu">
        <button
          className="ntf-menu-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDropdownToggle(notification._id);
          }}
        >
          <FiMoreHorizontal />
        </button>
        
        {dropdownOpen === notification._id && (
          <div className="ntf-dropdown">
            <button
              className="ntf-dropdown__item"
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsRead(notification._id, notification.read);
              }}
            >
              <FiCheck />
              {notification.read ? "Mark unread" : "Mark read"}
            </button>
            {notification.post?._id && (
              <button
                className="ntf-dropdown__item"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/posts/${notification.post._id}`);
                }}
              >
                <FiLink />
                View Post
              </button>
            )}
            <button
              className="ntf-dropdown__item ntf-dropdown__item--danger"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(notification._id);
              }}
            >
              <FiTrash2 />
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// EMPTY STATE COMPONENT
// ============================================================================

const EmptyState = ({ filter }) => (
  <div className="ntf-empty">
    <div className="ntf-empty__icon">
      <FiInbox />
    </div>
    <h3 className="ntf-empty__title">All caught up!</h3>
    <p className="ntf-empty__text">
      {filter === "all"
        ? "No notifications to display"
        : `No ${filter} notifications`}
    </p>
  </div>
);

// ============================================================================
// LOADING STATE COMPONENT
// ============================================================================

const LoadingState = () => (
  <div className="ntf-loading">
    <div className="ntf-spinner" />
    <p>Loading notifications...</p>
  </div>
);

// ============================================================================
// ERROR STATE COMPONENT
// ============================================================================

const ErrorState = ({ message, onRetry }) => (
  <div className="ntf-error">
    <FiAlertTriangle />
    <p>{message}</p>
    <button className="ntf-btn ntf-btn--primary" onClick={onRetry}>
      <FiRefreshCw /> Retry
    </button>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const NotificationsOverlay = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user } = useUserContext();
  const {
    unreadCount,
    updateUnreadCount,
    resetUnreadCount,
    notifications: contextNotifications,
    setNotifications: setContextNotifications,
    connectionStatus,
  } = useNotificationContext();

  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  const overlayRef = useRef(null);
  const listRef = useRef(null);

  // Fetch notifications
  const fetchNotifications = useCallback(
    async (pageNum = 1, append = false) => {
      try {
        if (pageNum === 1) setLoading(true);
        else setLoadingMore(true);

        const response = await notificationService.getNotifications(pageNum);
        const notificationsData = response.notifications || [];

        setRetryCount(0);
        setError(response.isStale ? "Using cached data. Network issues detected." : null);
        updateUnreadCount(response.unreadCount || 0);

        if (append) {
          const newNotifications = [...notifications, ...notificationsData];
          setNotifications(newNotifications);
          setContextNotifications(newNotifications);
        } else {
          setNotifications(notificationsData);
          setContextNotifications(notificationsData);
        }

        setHasMore(response.pagination?.page < response.pagination?.totalPages);
      } catch (err) {
        setError("Failed to load notifications. Please try again.");
        if (retryCount < 3) {
          const backoffTime = Math.pow(2, retryCount) * 1000;
          setTimeout(() => {
            setRetryCount((prev) => prev + 1);
            fetchNotifications(pageNum, append);
          }, backoffTime);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [notifications, updateUnreadCount, retryCount, setContextNotifications]
  );

  // Initialize on open
  useEffect(() => {
    if (isOpen) {
      if (contextNotifications?.length > 0) {
        setNotifications(contextNotifications);
        setLoading(false);
      }
      fetchNotifications();
      setPage(1);
      // Note: Don't call resetUnreadCount() here - it triggers markAllAsRead API
      // The unread count is already updated by fetchNotifications response
      // User can manually mark all as read with the button
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (overlayRef.current && !overlayRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  // Dropdown close handler
  useEffect(() => {
    const handleGlobalClick = (e) => {
      if (!e.target.closest(".ntf-item__menu")) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener("mousedown", handleGlobalClick);
    return () => document.removeEventListener("mousedown", handleGlobalClick);
  }, []);

  // Handlers
  const handleMarkAsRead = async (id, isRead) => {
    try {
      setActionLoading((prev) => ({ ...prev, [id]: true }));
      if (isRead) {
        setNotifications((prev) => prev.map((n) => (n._id === id ? { ...n, read: false } : n)));
        updateUnreadCount(unreadCount + 1);
      } else {
        const response = await notificationService.markAsRead(id);
        setNotifications((prev) => prev.map((n) => (n._id === id ? { ...n, read: true } : n)));
        updateUnreadCount(response.unreadCount || unreadCount - 1);
      }
      setDropdownOpen(null);
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setActionLoading((prev) => ({ ...prev, markAll: true }));
      await notificationService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      updateUnreadCount(0);
    } catch (err) {
      toast.error("Failed to mark all as read");
    } finally {
      setActionLoading((prev) => ({ ...prev, markAll: false }));
    }
  };

  const handleDelete = async (id) => {
    try {
      setActionLoading((prev) => ({ ...prev, [`delete_${id}`]: true }));
      const response = await notificationService.deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n._id !== id));
      updateUnreadCount(response.unreadCount || unreadCount);
      setDropdownOpen(null);
    } catch (err) {
      toast.error("Failed to delete notification");
    } finally {
      setActionLoading((prev) => ({ ...prev, [`delete_${id}`]: false }));
    }
  };

  const handleClearAll = async () => {
    try {
      setActionLoading((prev) => ({ ...prev, clearAll: true }));
      await notificationService.clearAllNotifications();
      setNotifications([]);
      updateUnreadCount(0);
    } catch (err) {
      toast.error("Failed to clear notifications");
    } finally {
      setActionLoading((prev) => ({ ...prev, clearAll: false }));
    }
  };

  const handleAction = async (notification, action) => {
    try {
      setActionLoading((prev) => ({ ...prev, [`action_${notification._id}`]: true }));
      const result = await notificationService.handleNotificationAction(notification._id, action);
      if (result.redirectUrl) {
        onClose();
        navigate(result.redirectUrl);
      }
      if (!notification.read) await handleMarkAsRead(notification._id, false);
    } catch (err) {
      toast.error(`Failed to ${action.replace(/_/g, " ")}`);
    } finally {
      setActionLoading((prev) => ({ ...prev, [`action_${notification._id}`]: false }));
    }
  };

  const handleCollaboratorAction = async (notification, action) => {
    try {
      setActionLoading((prev) => ({ ...prev, [`action_${notification._id}`]: true }));
      const { data = {} } = notification;

      switch (action) {
        case "accept_collab_request":
          await notificationService.handleNotificationAction(notification._id, "accept_collaboration", { requestId: data.requestId });
          toast.success("Collaboration request accepted! 🎉");
          const roomId = data.researchRoomId || notification.researchRoom;
          if (roomId) {
            onClose();
            navigate(`/research-room/${roomId}`);
          }
          break;
        case "decline_collab_request":
          await notificationService.handleNotificationAction(notification._id, "decline_collaboration", { requestId: data.requestId });
          toast.info("Collaboration request declined");
          break;
        case "accept_endorsement":
          await notificationService.handleNotificationAction(notification._id, "accept_endorsement", { endorsementId: data.endorsementId });
          toast.success(`Endorsement accepted! ✅`);
          break;
        case "decline_endorsement":
          await notificationService.handleNotificationAction(notification._id, "decline_endorsement", { endorsementId: data.endorsementId });
          toast.info("Endorsement declined");
          break;
        default:
          await notificationService.handleNotificationAction(notification._id, action);
      }

      if (!notification.read) await handleMarkAsRead(notification._id, false);
      setNotifications((prev) => prev.map((n) => (n._id === notification._id ? { ...n, actionTaken: action, read: true } : n)));
    } catch (err) {
      toast.error(`Failed to ${action.replace(/_/g, " ")}`);
    } finally {
      setActionLoading((prev) => ({ ...prev, [`action_${notification._id}`]: false }));
    }
  };

  const handleNavigate = async (notification) => {
    try {
      if (!notification.read) {
        const response = await notificationService.markAsRead(notification._id);
        setNotifications((prev) => prev.map((n) => (n._id === notification._id ? { ...n, read: true } : n)));
        updateUnreadCount(response.unreadCount || unreadCount - 1);
      }

      const { type, data = {} } = notification;
      const communityId = data?.communityId || notification.community;
      const roomId = data?.roomId || notification.researchRoom;
      const threadId = data?.threadId;
      const workshopId = notification.workshop?._id || data?.workshopId;

      // Navigation logic based on notification type
      if (notification.post?._id) {
        onClose();
        navigate(`/posts/${notification.post._id}`);
      } else if (type === "new_follower" && notification.sender?._id) {
        onClose();
        navigate(`/profile/${notification.sender._id}`);
      } else if (type === "collaboration_request" || type === "collaboration_response") {
        onClose();
        navigate("/collaborators");
      } else if (type === "message" && notification.sender?._id) {
        onClose();
        navigate(`/messages/${notification.sender._id}`);
      } else if (type.startsWith("community_")) {
        if (["community_moderation_ban", "community_member_removed"].includes(type)) {
          onClose();
          navigate("/communities");
        } else if (type === "community_join_request" && communityId) {
          onClose();
          navigate(`/communities/${communityId}/moderation/requests`);
        } else if (type === "community_new_report" && communityId) {
          onClose();
          navigate(`/communities/${communityId}/moderation/reports`);
        } else if (communityId && threadId) {
          onClose();
          navigate(`/communities/${communityId}/threads/${threadId}`);
        } else if (communityId) {
          onClose();
          navigate(`/communities/${communityId}`);
        }
      } else if (roomId && !["member_removed", "room_deleted"].includes(type)) {
        onClose();
        navigate(`/research-rooms/${roomId}`);
      } else if (isWorkshopNotificationType(type) && workshopId) {
        onClose();
        navigate(`/workshops/${workshopId}`);
      } else if (isCollaboratorNotificationType(type)) {
        const url = getCollaboratorActionUrl(notification);
        if (url) {
          onClose();
          navigate(url);
        }
      }
    } catch (err) {
      toast.error("Failed to process notification");
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight * 1.5 && hasMore && !loadingMore && !error) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchNotifications(nextPage, true);
    }
  };

  // Filter notifications
  const filteredNotifications = filter === "all"
    ? notifications
    : notifications.filter((n) => NOTIFICATION_TYPE_MAP[filter]?.includes(n.type));

  const groupedNotifications = groupNotificationsByTime(filteredNotifications);
  const hasNotifications = filteredNotifications.length > 0;

  if (!isOpen) return null;

  return (
    <div className="ntf-backdrop">
      <div className="ntf-panel" ref={overlayRef}>
        {/* Header */}
        <header className="ntf-header">
          <div className="ntf-header__left">
            <h2 className="ntf-header__title">
              <FiBell />
              Notifications
              {unreadCount > 0 && <span className="ntf-badge">{unreadCount}</span>}
            </h2>
          </div>
          <div className="ntf-header__right">
            {notifications.length > 0 && (
              <>
                <button
                  className="ntf-header__btn"
                  onClick={handleMarkAllAsRead}
                  disabled={actionLoading.markAll}
                  title="Mark all as read"
                >
                  <FiCheck />
                  <span className="ntf-header__btn-text">
                    {actionLoading.markAll ? "..." : "Read All"}
                  </span>
                </button>
                <button
                  className="ntf-header__btn"
                  onClick={handleClearAll}
                  disabled={actionLoading.clearAll}
                  title="Clear all"
                >
                  <FiArchive />
                  <span className="ntf-header__btn-text">
                    {actionLoading.clearAll ? "..." : "Clear"}
                  </span>
                </button>
              </>
            )}
            <button className="ntf-close" onClick={onClose} aria-label="Close">
              <FiX />
            </button>
          </div>
        </header>

        {/* Connection Status */}
        <ConnectionStatus status={connectionStatus} />

        {/* Filters */}
        <nav className="ntf-filters">
          {FILTER_OPTIONS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={`ntf-filter ${filter === id ? "ntf-filter--active" : ""}`}
              onClick={() => setFilter(id)}
            >
              <Icon />
              <span>{label}</span>
            </button>
          ))}
        </nav>

        {/* Content */}
        <div className="ntf-content" ref={listRef} onScroll={handleScroll}>
          {loading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState
              message={error}
              onRetry={() => {
                setError(null);
                fetchNotifications();
              }}
            />
          ) : !hasNotifications ? (
            <EmptyState filter={filter} />
          ) : (
            <>
              {Object.entries(groupedNotifications).map(
                ([key, notifs]) =>
                  notifs.length > 0 && (
                    <section key={key} className="ntf-group">
                      <h3 className="ntf-group__title">{TIME_LABELS[key]}</h3>
                      {notifs.map((notification) => (
                        <NotificationItem
                          key={notification._id}
                          notification={notification}
                          onMarkAsRead={handleMarkAsRead}
                          onDelete={handleDelete}
                          onAction={handleAction}
                          onCollaboratorAction={handleCollaboratorAction}
                          onNavigate={handleNavigate}
                          actionLoading={actionLoading}
                          dropdownOpen={dropdownOpen}
                          onDropdownToggle={setDropdownOpen}
                        />
                      ))}
                    </section>
                  )
              )}
              {loadingMore && (
                <div className="ntf-loading-more">
                  <div className="ntf-spinner ntf-spinner--small" />
                  <span>Loading more...</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsOverlay;
